import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import clsx from 'clsx';

import { ReactComponent as ReplaceIcon } from '../../../../assets/icons/exercises/replace.svg';
import DeleteActionButton from '../../../../components/designe/ActionButtons/DeleteActionButton';
import EditActionButton from '../../../../components/designe/ActionButtons/EditActionButton';
import AppExercisePropertyAvatar from '../../../../components/designe/app-entity-avatar/app-exercise-property-avatar/AppExercisePropertyAvatar';
import AppUserAvatar from '../../../../components/designe/app-entity-avatar/app-user-avatar/AppUserAvatar';
import AppImageGalleryContainer from '../../../../components/designe/app-image-gallery/AppImageGalleryContainer';
import AppButton from '../../../../components/designe/buttons/app-button/AppButton';
import ConfirmDialog from '../../../../components/reusable/confirm-dialog/ConfirmDialog';
import ViewField from '../../../../components/reusable/view-field/ViewField';
import { AvatarSize } from '../../../../shared/constants';
import { IExercise } from '../../../../types/interfaces/exercise';
import { IExerciseProperty } from '../../../../types/interfaces/exercise-property';
import { getUserViewName } from '../../../../utils/common/shared-util';
import { formatExerciseOptionDisplayName } from '../../../../utils/exercise-option-display';
import { useStyles } from './ExerciseSummary.styles';
import ReplaceExerciseDialog from './replace-exercise-dialog/ReplaceExerciseDialog';
import { useExercisePage } from './useExercisePage';

interface IExerciseSummaryProps {
  exercise: IExercise;
}

export default function ExerciseSummary({ exercise }: IExerciseSummaryProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const {
    onEditClick,
    onDelete,
    onReplaceExercise,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    showReplaceConfirmation,
    setShowReplaceConfirmation,
    replacementExerciseId,
    setReplacementExerciseId,
    replacementExerciseOptions,
    isReplacingExercise,
    onUserClick,
  } = useExercisePage(exercise);

  const renderExerciseProperty = useCallback(
    (property: 'equipment' | 'mainMuscleGroup') => {
      return (
        <Box className={classes.containerWithBackground}>
          <Box flex={1} maxWidth={'33,33%'}>
            <Typography variant={'label-text-md'}>
              {t(`exercises.${property}`)}
            </Typography>
          </Box>
          <Box
            display="flex"
            gap={1}
            alignItems="center"
            justifyContent="flex-end"
            flex={2}
            maxWidth={'66,66%'}>
            <AppExercisePropertyAvatar
              exerciseProperty={exercise?.[property]}
              size={AvatarSize.s}
            />
            <Typography variant={'text-lg-sb'}>
              {exercise?.[property]?.name}
            </Typography>
          </Box>
        </Box>
      );
    },
    [exercise, classes.containerWithBackground, t],
  );

  const renderAdditionalProperties = useCallback(
    (
      properties: IExerciseProperty[] | undefined,
      translationKey:
        | 'additionalMuscles'
        | 'additionalEquipments'
        | 'developedSkills',
    ) => {
      return (
        <Box
          className={clsx(
            classes.containerWithBackground,
            !properties?.length && classes.emptyBlock,
          )}>
          <Box flex={1} maxWidth={'33,33%'} display="flex">
            <Typography variant={'label-text-md'}>
              {t(`exercises.${translationKey}`)}
            </Typography>
          </Box>
          <Box
            display="flex"
            gap={1}
            flex={2}
            alignItems="flex-end"
            flexDirection="column"
            maxWidth={'66,66%'}>
            {properties?.length > 0 &&
              properties.map((property) => (
                <Box
                  key={property.id}
                  display="flex"
                  gap={1}
                  alignItems="center">
                  <AppExercisePropertyAvatar
                    exerciseProperty={property}
                    size={AvatarSize.s}
                  />
                  <Typography variant={'text-lg-sb'}>
                    {property?.name}
                  </Typography>
                </Box>
              ))}
            {!properties?.length && (
              <Typography variant={'text-lg-sb'}>
                {t(`exercises.no_${translationKey}_message`)}
              </Typography>
            )}
          </Box>
        </Box>
      );
    },
    [classes.containerWithBackground, t],
  );

  return (
    <>
      {!!exercise && (
        <Box className={classes.exerciseWrapper}>
          <Box
            display="flex"
            justifyContent="space-between"
            flexDirection="column"
            gap={0.5}>
            <Box
              display="flex"
              gap={0.5}
              justifyContent="space-between"
              alignItems="flex-start">
              <Typography variant="h1" className={classes.exerciseTitle}>
                {exercise?.title}
              </Typography>

              <Box display="flex" gap={0.5} flexShrink={0}>
                {exercise?.permissions?.canEdit && (
                  <EditActionButton onClick={onEditClick} />
                )}
                {exercise?.permissions?.canDelete && (
                  <DeleteActionButton
                    onClick={() => setShowDeleteConfirmation(true)}
                  />
                )}
              </Box>
            </Box>
            <Box
              marginTop={0.5}
              display="flex"
              gap={0.5}
              justifyContent="space-between">
              <Box marginTop={0.5}>
                <Typography variant="success-wrapped-sm">
                  {exercise?.category?.name}
                </Typography>
              </Box>

              {exercise?.permissions?.canBeReplaced && (
                <AppButton
                  variant="outlined"
                  icon={<ReplaceIcon />}
                  onClick={() => setShowReplaceConfirmation(true)}>
                  {t('exercises.replace')}
                </AppButton>
              )}
            </Box>
          </Box>

          {exercise?.files?.length > 0 && (
            <Box
              display="flex"
              justifyContent="center"
              className={classes.galleryWrapper}>
              <Box maxWidth="500px">
                <AppImageGalleryContainer
                  files={exercise?.files ?? []}
                  mainFileId={exercise?.avatarFileId}
                />
              </Box>
            </Box>
          )}
          <Box className={classes.optionsContainer}>
            <Box className={classes.optionsRow}>
              {renderExerciseProperty('equipment')}
              {renderAdditionalProperties(
                exercise?.additionalEquipments,
                'additionalEquipments',
              )}
            </Box>
            <Box className={classes.optionsRow}>
              {renderExerciseProperty('mainMuscleGroup')}
              {renderAdditionalProperties(
                exercise?.additionalMuscles,
                'additionalMuscles',
              )}
            </Box>
            <Box className={classes.optionsRow}>
              <Box
                flex={1}
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                gap={1}
                className={classes.containerWithBackground}>
                <Box flex={1} maxWidth={'33,33%'}>
                  <Typography variant={'label-text-md'}>
                    {t('exercises.formulaForCalculationLoad')}
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  gap={1}
                  flex={2}
                  justifyContent="flex-end"
                  maxWidth={'66,66%'}>
                  <Typography variant={'text-lg-sb'}>
                    {exercise.exerciseLoadFormula?.name ||
                      exercise.exerciseLoadFormula?.nameKey ||
                      t('exercises.noFormulaForCalculationLoad')}
                  </Typography>
                </Box>
              </Box>
              <Box flex={1} width="100%">
                {renderAdditionalProperties(
                  exercise?.developedSkills,
                  'developedSkills',
                )}
              </Box>
            </Box>
            <Box className={classes.optionsRow}>
              <Box
                flex={1}
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                gap={1}
                className={clsx(
                  classes.containerWithBackground,
                  !exercise?.options?.length && classes.emptyBlock,
                )}>
                <Box flex={1} maxWidth={'33,33%'}>
                  <Typography variant={'label-text-md'}>
                    {t('exercises.options')}
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  gap={1}
                  flex={2}
                  justifyContent="flex-end"
                  flexWrap="wrap"
                  maxWidth={'66,66%'}>
                  {exercise?.options?.length > 0 &&
                    exercise.options.map((option) => (
                      <Typography key={option.id} variant={'grey-wrapped-md'}>
                        {formatExerciseOptionDisplayName(
                          option.name || option.nameKey,
                          option.unit,
                          option.unitKey,
                        )}
                      </Typography>
                    ))}
                  {!exercise?.options?.length && (
                    <Typography variant={'text-lg-sb'}>
                      {t(`exercises.noOptionsMessage`)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          {exercise?.description && (
            <ViewField
              label={t('common.description')}
              value={exercise?.description}
            />
          )}

          {exercise.owner && (
            <Box className={classes.ownerWrapper}>
              <Typography variant={'label-text-md'}>
                {t('common.owner')}
              </Typography>

              <Box
                display={'flex'}
                gap={1}
                alignItems={'center'}
                marginTop={0.5}
                className={classes.linkWrapper}
                onClick={() => onUserClick(exercise.owner.id)}>
                <AppUserAvatar entity={exercise.owner} size={AvatarSize.s} />
                <Typography variant="text-lg">
                  {getUserViewName(exercise?.owner)}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      )}
      <ConfirmDialog
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        title={t('common.delete')}
        onSubmit={() => {
          onDelete();
        }}>
        {t('common.deleteQuestion')}
      </ConfirmDialog>
      <ReplaceExerciseDialog
        open={showReplaceConfirmation}
        onClose={() => setShowReplaceConfirmation(false)}
        currentExerciseTitle={exercise?.title ?? ''}
        replacementExerciseId={replacementExerciseId}
        setReplacementExerciseId={setReplacementExerciseId}
        replacementExerciseOptions={replacementExerciseOptions}
        isReplacingExercise={isReplacingExercise}
        onSubmit={onReplaceExercise}
      />
    </>
  );
}
